import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../App';
import { Lock, Mail, User, Zap, Shield, ArrowLeft, KeyRound, CheckCircle } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_cc5301f8-029c-4cb1-afaa-ab22368334bb/artifacts/yofpexsn_Logo%20-%20official%20300x300.png';

export default function LoginPage() {
  const [mode, setMode] = useState('login'); // 'login', 'register', 'forgot-password', 'reset-password', 'forgot-username'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  // Reset password
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [tokenReceived, setTokenReceived] = useState('');

  const { login, register, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { if (user) navigate('/'); }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setSuccess(''); setLoading(true);
    try {
      if (mode === 'register') { await register(name, email, password); }
      else { await login(email, password); }
      navigate('/');
    } catch (err) { setError(err.response?.data?.detail || 'Authentication failed.'); }
    finally { setLoading(false); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault(); setError(''); setSuccess(''); setLoading(true);
    try {
      const res = await api.post('/api/auth/forgot-password', { email: resetEmail });
      setSuccess(res.data.message);
      if (res.data.reset_token) {
        setTokenReceived(res.data.reset_token);
        setMode('reset-password');
      }
    } catch (err) { setError(err.response?.data?.detail || 'Something went wrong.'); }
    finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault(); setError(''); setSuccess('');
    if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }
    if (newPassword.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setLoading(true);
    try {
      const res = await api.post('/api/auth/reset-password', { email: resetEmail, token: resetToken || tokenReceived, new_password: newPassword });
      setSuccess(res.data.message);
      setTimeout(() => { setMode('login'); setSuccess(''); setError(''); }, 2000);
    } catch (err) { setError(err.response?.data?.detail || 'Reset failed.'); }
    finally { setLoading(false); }
  };

  const handleForgotUsername = async (e) => {
    e.preventDefault(); setError(''); setSuccess(''); setLoading(true);
    try {
      const res = await api.post('/api/auth/forgot-username', { email: resetEmail });
      if (res.data.status === 'found') setSuccess(res.data.message);
      else setError(res.data.message);
    } catch (err) { setError(err.response?.data?.detail || 'Something went wrong.'); }
    finally { setLoading(false); }
  };

  const switchMode = (newMode) => { setMode(newMode); setError(''); setSuccess(''); setTokenReceived(''); };

  return (
    <div className="login-page">
      <div className="login-left">
        <div className="login-hero">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, justifyContent: 'center' }}>
            <img src={LOGO_URL} alt="Panzix Global" style={{ width: 72, height: 72, borderRadius: 16 }} />
            <h1 style={{ fontSize: 38, margin: 0 }}>Panzix HR AI Workforce</h1>
          </div>
          <p style={{ marginTop: 0, fontSize: 18, opacity: 0.85 }}>Empowering People, Accelerating Potential</p>
          <div style={{ display: 'flex', gap: 14, marginTop: 32, justifyContent: 'center', flexWrap: 'wrap' }}>
            {['AI HR Agents', 'Smart Compliance', 'Auto Payroll', 'Talent Sourcing'].map(f => (
              <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.15)', padding: '8px 16px', borderRadius: 10, fontSize: 13, fontWeight: 500 }}>
                <Zap size={14} />{f}
              </div>
            ))}
          </div>
          <p style={{ marginTop: 40, fontSize: 12, opacity: 0.5, letterSpacing: 1 }}>POWERED BY PANZIX AI</p>
          <div style={{ display: 'flex', gap: 16, marginTop: 12, justifyContent: 'center' }}>
            <a href="https://customer-assets.emergentagent.com/job_ai-employees-6/artifacts/hz8a6ej1_privacy-policy-panzix-hr-ai-workforce.pdf" target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, textDecoration: 'none' }}>Privacy Policy</a>
            <a href="https://customer-assets.emergentagent.com/job_ai-employees-6/artifacts/c94tjqmn_terms-of-service-panzix-hr-ai-workforce.pdf" target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, textDecoration: 'none' }}>Terms of Service</a>
          </div>
        </div>
      </div>
      <div className="login-right">
        <div className="login-card slide-up">

          {/* Login / Register */}
          {(mode === 'login' || mode === 'register') && (
            <>
              <h2>{mode === 'register' ? 'Create Account' : 'Welcome Back'}</h2>
              <p className="subtitle">{mode === 'register' ? 'Start your HR transformation journey' : 'Sign in to your Panzix HR AI Workforce dashboard'}</p>
              {error && <div className="error-msg" data-testid="auth-error">{error}</div>}
              {success && <div style={{ background: '#dcfce7', color: '#166534', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}><CheckCircle size={16} />{success}</div>}
              <form onSubmit={handleSubmit}>
                {mode === 'register' && (
                  <div className="form-group">
                    <label><User size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />Full Name</label>
                    <input data-testid="register-name" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Jane Doe" required />
                  </div>
                )}
                <div className="form-group">
                  <label><Mail size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />Email</label>
                  <input data-testid="auth-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" required />
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span><Lock size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />Password</span>
                    {mode === 'login' && <button type="button" onClick={() => switchMode('forgot-password')} style={{ background: 'none', border: 'none', color: 'var(--panzix-green)', fontSize: 12, cursor: 'pointer', fontWeight: 600 }} data-testid="forgot-password-link">Forgot Password?</button>}
                  </label>
                  <input data-testid="auth-password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 8 characters" required minLength={8} />
                </div>
                <button type="submit" className="btn-primary" disabled={loading} data-testid="auth-submit">
                  {loading ? 'Please wait...' : mode === 'register' ? 'Create Account' : 'Sign In'}
                </button>
              </form>
              <div className="divider">or</div>
              <button className="btn-sso" data-testid="sso-btn" onClick={() => setError('SSO integration will be configured by your admin. Contact support for setup.')}>
                <Shield size={18} />Sign in with SSO
              </button>
              <div className="toggle-auth">
                {mode === 'register' ? 'Already have an account?' : "Don't have an account?"}{' '}
                <button onClick={() => switchMode(mode === 'register' ? 'login' : 'register')} data-testid="toggle-auth">
                  {mode === 'register' ? 'Sign In' : 'Create Account'}
                </button>
              </div>
              {mode === 'login' && (
                <div style={{ textAlign: 'center', marginTop: 8 }}>
                  <button onClick={() => switchMode('forgot-username')} style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 12, cursor: 'pointer' }} data-testid="forgot-username-link">
                    Forgot your username/email?
                  </button>
                </div>
              )}
            </>
          )}

          {/* Forgot Password */}
          {mode === 'forgot-password' && (
            <>
              <button onClick={() => switchMode('login')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, color: '#64748b', fontSize: 13, marginBottom: 16 }}>
                <ArrowLeft size={16} />Back to Sign In
              </button>
              <h2>Reset Password</h2>
              <p className="subtitle">Enter your email address and we'll send you a reset code.</p>
              {error && <div className="error-msg">{error}</div>}
              {success && <div style={{ background: '#dcfce7', color: '#166534', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}><CheckCircle size={16} />{success}</div>}
              <form onSubmit={handleForgotPassword}>
                <div className="form-group">
                  <label><Mail size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />Email Address</label>
                  <input data-testid="reset-email" type="email" value={resetEmail} onChange={e => setResetEmail(e.target.value)} placeholder="you@company.com" required />
                </div>
                <button type="submit" className="btn-primary" disabled={loading} data-testid="send-reset">
                  {loading ? 'Sending...' : 'Send Reset Code'}
                </button>
              </form>
              {tokenReceived && (
                <div style={{ marginTop: 16, padding: 14, background: '#fef3c7', borderRadius: 10, fontSize: 13 }}>
                  <p style={{ fontWeight: 600, color: '#92400e', marginBottom: 4 }}>Your reset code (check your email in production):</p>
                  <p style={{ fontSize: 20, fontWeight: 800, letterSpacing: 4, color: '#0f172a', textAlign: 'center' }}>{tokenReceived}</p>
                </div>
              )}
            </>
          )}

          {/* Reset Password */}
          {mode === 'reset-password' && (
            <>
              <button onClick={() => switchMode('login')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, color: '#64748b', fontSize: 13, marginBottom: 16 }}>
                <ArrowLeft size={16} />Back to Sign In
              </button>
              <h2><KeyRound size={22} style={{ marginRight: 8, verticalAlign: 'middle' }} />Enter New Password</h2>
              <p className="subtitle">Enter the reset code and your new password.</p>
              {error && <div className="error-msg">{error}</div>}
              {success && <div style={{ background: '#dcfce7', color: '#166534', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}><CheckCircle size={16} />{success}</div>}
              <form onSubmit={handleResetPassword}>
                <div className="form-group">
                  <label>Reset Code</label>
                  <input data-testid="reset-token" type="text" value={resetToken || tokenReceived} onChange={e => setResetToken(e.target.value)} placeholder="Enter reset code" required
                    style={{ letterSpacing: 3, fontSize: 18, fontWeight: 700, textAlign: 'center', textTransform: 'uppercase' }} />
                </div>
                <div className="form-group">
                  <label><Lock size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />New Password</label>
                  <input data-testid="new-reset-password" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min 8 characters" required minLength={8} />
                </div>
                <div className="form-group">
                  <label><Lock size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />Confirm Password</label>
                  <input data-testid="confirm-reset-password" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter new password" required />
                </div>
                <button type="submit" className="btn-primary" disabled={loading} data-testid="reset-submit">
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            </>
          )}

          {/* Forgot Username */}
          {mode === 'forgot-username' && (
            <>
              <button onClick={() => switchMode('login')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, color: '#64748b', fontSize: 13, marginBottom: 16 }}>
                <ArrowLeft size={16} />Back to Sign In
              </button>
              <h2>Find Your Account</h2>
              <p className="subtitle">Enter your email address to find your account.</p>
              {error && <div className="error-msg">{error}</div>}
              {success && <div style={{ background: '#dcfce7', color: '#166534', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}><CheckCircle size={16} />{success}</div>}
              <form onSubmit={handleForgotUsername}>
                <div className="form-group">
                  <label><Mail size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />Email Address</label>
                  <input data-testid="find-email" type="email" value={resetEmail} onChange={e => setResetEmail(e.target.value)} placeholder="you@company.com" required />
                </div>
                <button type="submit" className="btn-primary" disabled={loading} data-testid="find-account">
                  {loading ? 'Searching...' : 'Find Account'}
                </button>
              </form>
            </>
          )}

        </div>
      </div>
    </div>
  );
}
